/**
 * AccountSettingsModal — Self-contained Account Settings overlay.
 *
 * Rendered at the App level so it can be opened from the avatar menu (or
 * anywhere else) without navigating away from the current page.
 *
 * The modal fetches its own user data on mount and manages its own form
 * state. Three tabs: Profile, App Settings, Privacy & Security.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { i18n } from '../../i18n';
import {
  fetchUserByGuid,
  updateUserProfile,
  checkScreenNameAvailable,
  type SecurityUserResponse,
  type UpdateUserProfileRequest,
  fetchAllCommunityMemberships,
} from '../../api/security';
import {
  fetchAllCommunityEntitiesAdmin,
  fetchAllRelationships,
  communityEntityToSeedShape,
  relationshipToSeedShape,
} from '../../api/communityMetadata';
import { pathFromLeafGuid, pathDisplay } from '../../utils/entityPath';
import { clearNumberFormatCache } from '../../utils/numberFormat';
import { useTheme } from '../../theme';
import { useUserThemeApi } from '../../api/userTheme';
import { getCurrentUserGuid } from '../../api/client';
import { useAccountSettings } from './AccountSettingsContext';
import { isNotificationSoundEnabled, setNotificationSoundEnabled } from '../../notifications/notificationSound';
import { useColorBlindMode } from '../../theme/colorBlindMode';
import {
  getDefaultCommunityView,
  setDefaultCommunityView,
  updateDefaultCommunityViewOnServer,
} from '../../state/defaultCommunityView';
import { getSearchBarMode, setSearchBarMode, type SearchBarMode } from '../../state/searchBarPreference';
import { communityMembershipStore, communityConfigStore, communityRoleStore } from '../../Data/store';
import { fetchCommunitySlotOccupancies } from '../../api/communityRoleSlots';
import { resolveCommunityAncestors } from '../../Data/ProjectConfig/projectConfigOverrides';
import TotpSetup from '../TotpSetup/TotpSetup';
import EulaModal from '../EulaModal/EulaModal';
import { EULA_CURRENT_VERSION } from '../../content/legal/eulaConstants';
import { useAuth } from '../../auth/AuthContext';
import GeoLocationPicker from '../GeoLocationPicker/GeoLocationPicker';

/* ------------------------------------------------------------------ */
/*  Shared inline-style constants                                       */
/* ------------------------------------------------------------------ */

const SETTINGS_SELECT: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: 8,
  border: '1px solid var(--border-default)',
  background: 'var(--bg-secondary)',
  color: 'var(--text-primary)',
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  paddingInlineEnd: 44,
};

const SETTINGS_LABEL: React.CSSProperties = {
  fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6,
};

const SETTINGS_CARD: React.CSSProperties = {
  marginBottom: 14,
};

const ST = {
  section: {
    background: 'var(--bg-secondary)', borderRadius: 8, padding: 24, marginBottom: 20,
  } as React.CSSProperties,
  sectionTitle: {
    fontSize: 15, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 16,
    letterSpacing: '0.03em', textTransform: 'uppercase' as const,
  } as React.CSSProperties,
  fieldRow: {
    display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16,
  } as React.CSSProperties,
  fieldLabel: {
    width: 140, flexShrink: 0, fontSize: 13, color: 'var(--text-tertiary)', paddingTop: 8, textAlign: 'right' as const,
  } as React.CSSProperties,
  fieldInput: {
    flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border-default)', borderRadius: 6,
    padding: '8px 12px', fontSize: 14, color: 'var(--text-primary)', outline: 'none',
  } as React.CSSProperties,
  fieldTextarea: {
    flex: 1, background: 'var(--bg-secondary)', border: '1px solid var(--border-default)', borderRadius: 6,
    padding: '8px 12px', fontSize: 14, color: 'var(--text-primary)', outline: 'none',
    minHeight: 80, resize: 'vertical' as const, fontFamily: 'inherit',
  } as React.CSSProperties,
  saveRow: {
    display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4,
  } as React.CSSProperties,
  btnPrimary: {
    background: 'var(--accent-primary)', color: '#fff', border: 'none', borderRadius: 6,
    padding: '8px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  } as React.CSSProperties,
  btnSecondary: {
    background: 'var(--bg-active)', color: 'var(--text-secondary)', border: 'none', borderRadius: 6,
    padding: '8px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer',
  } as React.CSSProperties,
  statusMsg: {
    fontSize: 13, padding: '6px 0', textAlign: 'right' as const,
  } as React.CSSProperties,
};

/* ------------------------------------------------------------------ */
/*  Geo search helper (lightweight version)                             */
/* ------------------------------------------------------------------ */

interface GeoEntity {
  entity_guid: string;
  name: string;
  entity_type: string;
  code?: string;
}

function buildGeoIndex(entities: GeoEntity[], relationships: any[]) {
  // DEV-963: communityEntityToSeedShape expects the full CommunityEntityResponse,
  // but `entities` here is the lightweight GeoEntity shape. The helper only
  // reads {entity_guid, name, entity_type, code} which all exist on GeoEntity.
  const seeds = entities.map((e) => communityEntityToSeedShape(e as any));
  const relSeeds = relationships.map(relationshipToSeedShape);
  return { seeds, relSeeds, entities };
}

/*
  Geo search + label resolution moved to `src/utils/geoLocationSearch.ts`
  and the picker UI to `src/Components/GeoLocationPicker/`. Three surfaces
  (Project Settings, Account Settings, Community Settings) now share the
  same algorithm and visual treatment — one place to fix bugs, one place
  to improve the algorithm.
*/

/* ------------------------------------------------------------------ */
/*  Main component                                                      */
/* ------------------------------------------------------------------ */

export default function AccountSettingsModal() {
  const { isOpen, close, initialTab, activeUserGuid } = useAccountSettings();
  const { t } = useTranslation();
  const { user: authUser } = useAuth();

  // ── Data loading ──
  const [user, setUser] = useState<SecurityUserResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [communities, setCommunities] = useState<GeoEntity[]>([]);
  const [relationships, setRelationships] = useState<any[]>([]);

  // ── Tab state ──
  // Initial tab comes from context — `openToTab('privacy')` lands on
  // the Privacy & Security tab, used by the enable_2fa milestone CTA.
  const [activeTab, setActiveTab] = useState<0 | 1 | 2>(initialTab);

  // Reset to the requested tab whenever the modal opens fresh. Without
  // this, a user who opens to Privacy, switches to Profile, closes, and
  // re-opens via "Edit profile" CTA would still land on Privacy.
  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [isOpen, initialTab]);

  // ── EULA review modal state (Privacy & Security tab) ──
  // When the user opens the agreement from this surface, the modal
  // is in "review" mode if they've already accepted the current
  // version (no Accept action needed) or "re-accept" mode if a new
  // version has been published since their last acceptance. The
  // EulaModal component handles both via its always-on Accept
  // button — accepting the same version is idempotent.
  const [eulaOpen, setEulaOpen] = useState(false);

  // ── Profile form state ──
  const [formName, setFormName] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formDateOfBirth, setFormDateOfBirth] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formScreenName, setFormScreenName] = useState('');
  const [screenNameStatus, setScreenNameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle');
  const [formPrimaryLocation, setFormPrimaryLocation] = useState('');
  const [formOperationalLocation, setFormOperationalLocation] = useState('');
  const [formShowHome, setFormShowHome] = useState(false);
  const [formShowOp, setFormShowOp] = useState(false);
  const [formIsSearchable, setFormIsSearchable] = useState(false);
  // Feed Member Discovery privacy dials (migration 045).
  const [formContactability, setFormContactability] = useState('members_of_my_communities');
  const [formDiscoveryAmp, setFormDiscoveryAmp] = useState(true);
  const [formCountryCode, setFormCountryCode] = useState('');

  // Location search
  // Location picker state (query, suggestions, label) is now owned by
  // the shared <GeoLocationPicker> component — see Profile tab below.

  // ── Preferences state ──
  const { themeName: savedTheme, setThemeName, availableThemes } = useTheme();
  const { updateTheme } = useUserThemeApi();
  const savedLang = i18n.language?.split('-')[0] || 'en';
  const savedCalendar = (() => { try { return localStorage.getItem('l4e_calendar') || 'gregorian'; } catch { return 'gregorian'; } })();
  const [pendingTheme, setPendingTheme] = useState(savedTheme);
  const [pendingLang, setPendingLang] = useState(savedLang);
  const [pendingCalendar, setPendingCalendar] = useState(savedCalendar);

  // ── Accessibility ──
  const colorBlindMode = useColorBlindMode();
  const [soundEnabled, setSoundEnabled] = useState(isNotificationSoundEnabled());

  // ── Save state ──
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  /*
    Use the "viewed-as" user GUID pushed into context by App.tsx — not
    `getCurrentUserGuid()` from api/client. The two diverge when an
    admin uses the gear-menu user-switcher: getCurrentUserGuid()
    returns the JWT-authenticated user (the admin) while
    activeUserGuid reflects the gear-menu selection. The modal should
    display the user the admin is viewing as.

    Falls back to getCurrentUserGuid() if context isn't populated yet
    (mount order edge cases) so we never show an empty state.
  */
  const currentUserGuid = activeUserGuid || getCurrentUserGuid();

  // DEV-1019 — per-user namespaced Default Community View preference.
  // Must be declared AFTER currentUserGuid. Defaults to 'overview' when
  // no saved value so the <select> always has a matching <option>.
  const savedCommunityView = getDefaultCommunityView(currentUserGuid) || 'overview';
  const [pendingCommunityView, setPendingCommunityView] = useState<string>(savedCommunityView);

  // DEV-1389 — per-user topbar search display preference (device-local).
  const savedSearchBar = getSearchBarMode(currentUserGuid);
  const [pendingSearchBar, setPendingSearchBar] = useState<SearchBarMode>(savedSearchBar);

  // ── Screen name validation ──
  const screenNameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!user || !formScreenName || formScreenName === (user.screenName || '')) {
      setScreenNameStatus('idle');
      return;
    }
    setScreenNameStatus('checking');
    if (screenNameTimerRef.current) clearTimeout(screenNameTimerRef.current);
    screenNameTimerRef.current = setTimeout(async () => {
      try {
        const available = await checkScreenNameAvailable(formScreenName);
        setScreenNameStatus(available ? 'available' : 'taken');
      } catch {
        setScreenNameStatus('idle');
      }
    }, 500);
    return () => { if (screenNameTimerRef.current) clearTimeout(screenNameTimerRef.current); };
  }, [formScreenName, user]);

  // ── Load data when modal opens ──
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setActiveTab(0);
    setSaveStatus('idle');

    // Reset prefs
    setPendingTheme(savedTheme);
    setPendingLang(savedLang);
    setPendingCalendar((() => { try { return localStorage.getItem('l4e_calendar') || 'gregorian'; } catch { return 'gregorian'; } })());
    setPendingCommunityView(getDefaultCommunityView(currentUserGuid) || 'overview');
    setPendingSearchBar(getSearchBarMode(currentUserGuid));

    Promise.all([
      fetchUserByGuid(currentUserGuid),
      fetchAllCommunityEntitiesAdmin(),
      fetchAllRelationships(),
    ])
      .then(([userData, communityData, relationshipData]) => {
        if (cancelled) return;
        setUser(userData);
        // The API returns CommunityEntityResponse with camelCase keys
        // (`entityType`, `entityGuid`, …) but every downstream consumer in
        // this codebase — including the shared `isGeoEntity` predicate in
        // `geoLocationSearch.ts` — expects the snake_case "seed" shape
        // (`entity_type`, `entity_guid`, …). Without these transforms the
        // typeahead can't recognize geo entities (chips show "Unknown
        // community" and search returns nothing). Project Settings already
        // applies the same transforms; this brings Account Settings into
        // parity. Same fix needed in CommunitySettingsModal.
        setCommunities((communityData as any[]).map(communityEntityToSeedShape) as any);
        setRelationships((relationshipData as any[]).map(relationshipToSeedShape) as any);

        // Seed form fields
        setFormName(userData.name || '');
        setFormDisplayName(userData.displayName || '');
        setFormDateOfBirth(userData.dateOfBirth || '');
        setFormEmail(userData.email || '');
        setFormScreenName(userData.screenName || '');
        setFormCountryCode(userData.countryCode || '');
        setFormPrimaryLocation(userData.primaryLocationEntityGuid || '');
        setFormOperationalLocation(userData.operationalCommunityGuid || '');
        setFormShowHome(userData.showHomeCommunity ?? false);
        setFormShowOp(userData.showOperationalCommunity ?? false);
        setFormIsSearchable(userData.isSearchable ?? false);
        setFormContactability(userData.contactability ?? 'members_of_my_communities');
        setFormDiscoveryAmp(userData.discoveryAmplification ?? true);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [isOpen, currentUserGuid, savedTheme, savedLang]);

  // ── Community map ──
  const communityMap = useMemo(() => {
    const map = new Map<string, any>();
    for (const c of communities) map.set((c.entity_guid || '').toUpperCase(), c);
    return map;
  }, [communities]);

  // ── Location suggestions ──
  // Suggestions and label resolution are computed inside
  // <GeoLocationPicker> using the shared geoLocationSearch helpers.
  // Removed previously-inline implementations.

  // ── Dirty check ──
  const isDirty = useMemo(() => {
    const profileDirty = !user ? false : (
      formName !== (user.name || '') ||
      formDisplayName !== (user.displayName || '') ||
      formDateOfBirth !== (user.dateOfBirth ?? '') ||
      formEmail !== (user.email || '') ||
      formPrimaryLocation !== (user.primaryLocationEntityGuid || '') ||
      formScreenName !== (user.screenName || '') ||
      formCountryCode !== (user.countryCode || '') ||
      formOperationalLocation !== (user.operationalCommunityGuid || '') ||
      formShowHome !== (user.showHomeCommunity ?? false) ||
      formShowOp !== (user.showOperationalCommunity ?? false) ||
      formIsSearchable !== (user.isSearchable ?? false) ||
      formContactability !== (user.contactability ?? 'members_of_my_communities') ||
      formDiscoveryAmp !== (user.discoveryAmplification ?? true)
    );
    const prefsDirty = (
      pendingTheme !== savedTheme ||
      pendingLang !== savedLang ||
      pendingCalendar !== savedCalendar ||
      pendingCommunityView !== savedCommunityView ||
      pendingSearchBar !== savedSearchBar
    );
    return profileDirty || prefsDirty;
    // DEV-945 cleanup: pendingProjectView / savedProjectView removed.
    // The App Settings Default Project View dropdown was retired with
    // DEV-945; the corresponding state declarations and switcher were
    // deleted but these dirty-check references were missed, throwing
    // ReferenceError on modal mount.
  }, [user, formName, formDisplayName, formDateOfBirth, formEmail, formPrimaryLocation,
    formScreenName, formCountryCode, formOperationalLocation, formShowHome,
    formShowOp, formIsSearchable, formContactability, formDiscoveryAmp,
    pendingTheme, savedTheme, pendingLang,
    savedLang, pendingCalendar, savedCalendar,
    pendingCommunityView, savedCommunityView,
    pendingSearchBar, savedSearchBar]);

  // ── Save handler ──
  const handleSave = useCallback(async () => {
    if (!currentUserGuid || !isDirty) return;
    if (screenNameStatus === 'taken') return;
    setSaving(true);
    setSaveStatus('idle');
    try {
      const req: UpdateUserProfileRequest = {
        name: formName,
        displayName: formDisplayName,
        dateOfBirth: formDateOfBirth || null,
        email: formEmail,
        // `notes` intentionally OMITTED — the bio is owned by the profile
        // About tab's inline editor; the backend leaves the field untouched
        // when absent (SecurityController: `if (request.Notes is not null)`).
        primaryLocationEntityGuid: formPrimaryLocation || null,
        screenName: formScreenName || '',
        countryCode: formCountryCode || '',
        operationalCommunityGuid: formOperationalLocation || null,
        showHomeCommunity: formShowHome,
        showOperationalCommunity: formShowOp,
        isSearchable: formIsSearchable,
        contactability: formContactability,
        discoveryAmplification: formDiscoveryAmp,
        profileImageUrl: user?.profileImageUrl || null,
        heroImageUrl: user?.heroImageUrl || null,
      };
      const updated = await updateUserProfile(currentUserGuid, req);
      setUser(updated);

      // DEV-1430 — signal other surfaces (e.g. the open UserProfilePage) to
      // refetch so a name/profile change shows immediately instead of only
      // after a navigate-away/back remount. Mirrors the l4e_calendar_changed /
      // l4e_community_enablers_persisted refresh-event pattern.
      try {
        window.dispatchEvent(
          new CustomEvent('l4e_profile_updated', { detail: { userGuid: currentUserGuid } }),
        );
      } catch { /* ignore */ }

      if (pendingTheme !== savedTheme) {
        setThemeName(pendingTheme);
        try { await updateTheme(pendingTheme); } catch { /* localStorage still has it */ }
      }
      if (pendingLang !== savedLang) {
        i18n.changeLanguage(pendingLang);
        try { localStorage.setItem('l4e_language', pendingLang); } catch { /* ignore */ }
        clearNumberFormatCache();
        const isRtlLang = ['ar', 'he', 'fa', 'ur'].includes(pendingLang);
        document.documentElement.dir = isRtlLang ? 'rtl' : 'ltr';
      }
      if (pendingCalendar !== savedCalendar) {
        try { localStorage.setItem('l4e_calendar', pendingCalendar); } catch { /* ignore */ }
        window.dispatchEvent(new CustomEvent('l4e_calendar_changed', { detail: pendingCalendar }));
      }
      if (pendingCommunityView !== savedCommunityView) {
        // DEV-1448: optimistic cache first, then the server value (the
        // authority — cross-device per the DEV-1443 lesson). Inside the
        // try block deliberately: a failed server write surfaces as the
        // modal's error state instead of silently staying device-local.
        setDefaultCommunityView(pendingCommunityView, currentUserGuid);
        await updateDefaultCommunityViewOnServer(pendingCommunityView, currentUserGuid);
      }
      if (pendingSearchBar !== savedSearchBar) {
        setSearchBarMode(pendingSearchBar, currentUserGuid);
      }

      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch {
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  }, [currentUserGuid, isDirty, screenNameStatus, formName, formDisplayName, formDateOfBirth,
    formEmail, formPrimaryLocation, formScreenName, formCountryCode,
    formOperationalLocation, formShowHome, formShowOp, formIsSearchable,
    formContactability, formDiscoveryAmp,
    user, pendingTheme, savedTheme, pendingLang, savedLang, pendingCalendar,
    savedCalendar, pendingCommunityView,
    savedCommunityView, pendingSearchBar, savedSearchBar, setThemeName, updateTheme]);

  // ── Reset handler ──
  const handleReset = useCallback(() => {
    if (!user) return;
    setFormName(user.name || '');
    setFormDisplayName(user.displayName || '');
    setFormDateOfBirth(user.dateOfBirth || '');
    setFormEmail(user.email || '');
    setFormScreenName(user.screenName || '');
    setFormCountryCode(user.countryCode || '');
    setFormPrimaryLocation(user.primaryLocationEntityGuid || '');
    setFormOperationalLocation(user.operationalCommunityGuid || '');
    setFormShowHome(user.showHomeCommunity ?? false);
    setFormShowOp(user.showOperationalCommunity ?? false);
    setFormIsSearchable(user.isSearchable ?? false);
    setFormContactability(user.contactability ?? 'members_of_my_communities');
    setFormDiscoveryAmp(user.discoveryAmplification ?? true);
    setPendingTheme(savedTheme);
    setPendingLang(savedLang);
    setPendingCalendar(savedCalendar);
    setPendingCommunityView(savedCommunityView);
    setSaveStatus('idle');
  }, [user, savedTheme, savedLang, savedCalendar, savedCommunityView]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 200000,
        background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div style={{
        background: 'var(--bg-tertiary)', borderRadius: 12, padding: 0,
        width: '90%', maxWidth: 700, height: '80vh', maxHeight: 960,
        display: 'flex', flexDirection: 'column' as const,
        border: '1px solid var(--border-default)',
        boxShadow: 'var(--shadow-lg)',
      }}>
        {/* Modal header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px 16px', borderBottom: '1px solid var(--border-subtle)',
          flexShrink: 0, borderRadius: '12px 12px 0 0',
        }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>
            {t('userProfile:section.accountSettings')}
          </h2>
          <button type="button" onClick={close} style={{
            background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', fontSize: 20, padding: 4, lineHeight: 1,
          }}>&times;</button>
        </div>

        {/* Tab bar */}
        <div style={{
          display: 'flex', gap: 0, padding: '0 24px',
          borderBottom: '1px solid var(--border-subtle)', flexShrink: 0,
        }}>
          {([
            { idx: 0 as const, label: t('userProfile:accountTabs.profile') },
            { idx: 1 as const, label: t('userProfile:accountTabs.appSettings') },
            { idx: 2 as const, label: t('userProfile:accountTabs.privacySecurity') },
          ]).map(({ idx, label }) => (
            <button
              key={idx}
              type="button"
              onClick={() => setActiveTab(idx)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '10px 16px',
                fontSize: 13, fontWeight: activeTab === idx ? 700 : 500,
                color: activeTab === idx ? 'var(--accent-text, #f59e0b)' : 'var(--text-secondary)',
                borderBottom: activeTab === idx ? '2px solid var(--accent-text, #f59e0b)' : '2px solid transparent',
                marginBottom: -1,
                transition: 'color 150ms ease, border-color 150ms ease',
                whiteSpace: 'nowrap' as const,
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Modal body — scrollable */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px 24px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--text-tertiary)', fontSize: 15 }}>
              Loading...
            </div>
          ) : (
            <>
              {/* ═══ TAB 0: Profile ═══ */}
              {activeTab === 0 && (
                <ProfileTabContent
                  user={user}
                  formName={formName} setFormName={setFormName}
                  formDisplayName={formDisplayName} setFormDisplayName={setFormDisplayName}
                  formDateOfBirth={formDateOfBirth} setFormDateOfBirth={setFormDateOfBirth}
                  formEmail={formEmail} setFormEmail={setFormEmail}
                  formScreenName={formScreenName} setFormScreenName={setFormScreenName}
                  screenNameStatus={screenNameStatus}
                  formPrimaryLocation={formPrimaryLocation} setFormPrimaryLocation={setFormPrimaryLocation}
                  formOperationalLocation={formOperationalLocation} setFormOperationalLocation={setFormOperationalLocation}
                  communities={communities}
                  relationships={relationships}
                  formShowHome={formShowHome} setFormShowHome={setFormShowHome}
                  formShowOp={formShowOp} setFormShowOp={setFormShowOp}
                  formIsSearchable={formIsSearchable} setFormIsSearchable={setFormIsSearchable}
                  formContactability={formContactability} setFormContactability={setFormContactability}
                  formDiscoveryAmp={formDiscoveryAmp} setFormDiscoveryAmp={setFormDiscoveryAmp}
                />
              )}

              {/* ═══ TAB 1: App Settings ═══ */}
              {activeTab === 1 && (
                <>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 10 }}>
                    {t('userProfile:appSettings.appearanceTitle')}
                  </div>
                  <ThemeSwitcher value={pendingTheme} onChange={setPendingTheme} availableThemes={availableThemes} />
                  <LanguageSwitcher value={pendingLang} onChange={setPendingLang} onCalendarHint={setPendingCalendar} />
                  <CalendarPreference value={pendingCalendar} onChange={setPendingCalendar} />

                  <div style={{ borderTop: '1px solid var(--border-subtle)', margin: '6px 0 16px' }} />

                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 10 }}>
                    {t('userProfile:appSettings.accessibilityTitle')}
                  </div>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                    padding: '10px 14px', borderRadius: 8, marginBottom: 14,
                    background: 'var(--bg-hover)', border: '1px solid var(--border-subtle)',
                  }}>
                    <input
                      type="checkbox"
                      checked={colorBlindMode.enabled}
                      onChange={(e) => colorBlindMode.setEnabled(e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: 'var(--accent-primary)', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <div style={{ display: 'grid', gap: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{t('userProfile:settings.colorBlindLabel')}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.35 }}>
                        {t('userProfile:settings.colorBlindDesc')}
                      </div>
                      <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                        {(['completed', 'green', 'yellow', 'red', 'grey', 'notApplicable'] as const).map((bucket) => (
                          <div
                            key={bucket}
                            style={{
                              width: 16, height: 16, borderRadius: 4,
                              background: colorBlindMode.palette[bucket],
                              border: '1px solid var(--border-subtle)',
                            }}
                            title={bucket}
                          />
                        ))}
                      </div>
                    </div>
                  </label>

                  <div style={{ borderTop: '1px solid var(--border-subtle)', margin: '6px 0 16px' }} />

                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 10 }}>
                    {t('userProfile:appSettings.defaultViewsTitle')}
                  </div>
                  <StartPageSwitcher
                    value={pendingCommunityView}
                    onChange={setPendingCommunityView}
                    userGuid={currentUserGuid}
                    communityMap={communityMap}
                    homeCommunityGuid={formPrimaryLocation || null}
                    operationalCommunityGuid={formOperationalLocation || null}
                    entities={communities}
                    relationships={relationships}
                  />

                  {/* DEV-1389 — topbar search display preference. */}
                  <div style={{ marginBlockStart: 14 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', marginBlockEnd: 4 }}>
                      {t('userProfile:appSettings.searchBarLabel')}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBlockEnd: 8 }}>
                      {t('userProfile:appSettings.searchBarHelp')}
                    </div>
                    <select
                      value={pendingSearchBar}
                      onChange={(e) => setPendingSearchBar(e.target.value as SearchBarMode)}
                      style={{
                        width: '100%', padding: '8px 10px', borderRadius: 8,
                        border: '1px solid var(--border-default)', background: 'var(--bg-hover)',
                        color: 'var(--text-primary)', fontSize: 13,
                      }}
                    >
                      <option value="bar">{t('userProfile:appSettings.searchBarOptionBar')}</option>
                      <option value="icon">{t('userProfile:appSettings.searchBarOptionIcon')}</option>
                    </select>
                  </div>

                  <div style={{ borderTop: '1px solid var(--border-subtle)', margin: '6px 0 16px' }} />

                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: '0.06em', color: 'var(--text-muted)', marginBottom: 10 }}>
                    {t('userProfile:appSettings.notificationsTitle')}
                  </div>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                    padding: '10px 14px', borderRadius: 8,
                    background: 'var(--bg-hover)', border: '1px solid var(--border-subtle)',
                  }}>
                    <input
                      type="checkbox"
                      checked={soundEnabled}
                      onChange={(e) => {
                        const next = e.target.checked;
                        setSoundEnabled(next);
                        setNotificationSoundEnabled(next);
                      }}
                      style={{ width: 16, height: 16, accentColor: 'var(--accent-primary)', cursor: 'pointer', flexShrink: 0 }}
                    />
                    <div style={{ display: 'grid', gap: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{t('userProfile:settings.soundLabel')}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.35 }}>
                        {t('userProfile:settings.soundDesc')}
                      </div>
                    </div>
                  </label>
                </>
              )}

              {/* ═══ TAB 2: Privacy & Security ═══ */}
              {activeTab === 2 && (
                <div>
                  <TotpSetup isSystemAdmin={!!authUser?.isSystemAdmin} />

                  {/*
                    EULA review card — DEV-906. Replaced the dimmed
                    placeholder with a clickable surface that opens
                    the EulaModal. Status line shows the user's
                    accepted version + date so they know what they
                    agreed to. If a newer version exists than what
                    they've accepted, an explicit "update available"
                    badge nudges them to re-accept.
                  */}
                  {(() => {
                    const acceptedAt = user?.eulaAcceptedAt;
                    const acceptedVersion = user?.eulaVersionAccepted;
                    const isCurrent =
                      typeof acceptedVersion === 'number'
                      && acceptedVersion >= EULA_CURRENT_VERSION;
                    const acceptedDateText = acceptedAt
                      ? new Date(acceptedAt).toLocaleDateString()
                      : null;
                    return (
                      <div style={{
                        padding: '12px 14px', borderRadius: 8, marginBottom: 8,
                        background: 'var(--bg-hover)', border: '1px solid var(--border-subtle)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        gap: 12,
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2 }}>
                            {t('userProfile:privacy.eulaTitle')}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.35 }}>
                            {t('userProfile:privacy.eulaDesc')}
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
                            {acceptedDateText && isCurrent && (
                              <>{t('userProfile:privacy.eulaAcceptedOn', { date: acceptedDateText, version: acceptedVersion })}</>
                            )}
                            {acceptedDateText && !isCurrent && (
                              <span style={{ color: 'var(--color-warning)' }}>
                                {t('userProfile:privacy.eulaUpdateAvailable', { current: EULA_CURRENT_VERSION })}
                              </span>
                            )}
                            {!acceptedDateText && (
                              <span style={{ color: 'var(--color-warning)' }}>
                                {t('userProfile:privacy.eulaNotAccepted')}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setEulaOpen(true)}
                          style={{
                            flexShrink: 0,
                            padding: '6px 14px',
                            background: 'transparent',
                            color: 'var(--text-primary)',
                            border: '1px solid var(--border-default)',
                            borderRadius: 6,
                            fontSize: 12,
                            fontWeight: 500,
                            cursor: 'pointer',
                          }}
                        >
                          {t('userProfile:privacy.eulaReview')}
                        </button>
                      </div>
                    );
                  })()}

                  <div style={{
                    padding: '12px 14px', borderRadius: 8, marginBottom: 8,
                    background: 'var(--bg-hover)', border: '1px solid var(--border-subtle)',
                    opacity: 0.5,
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2 }}>
                      {t('userProfile:privacy.privacyPolicyTitle')}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.35 }}>
                      {t('userProfile:privacy.privacyPolicyDesc')}
                    </div>
                  </div>

                  <div style={{
                    padding: '12px 14px', borderRadius: 8, marginBottom: 8,
                    background: 'var(--bg-hover)', border: '1px solid var(--border-subtle)',
                    opacity: 0.5,
                  }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', marginBottom: 2 }}>
                      {t('userProfile:privacy.dataExportTitle')}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.35 }}>
                      {t('userProfile:privacy.dataExportDesc')}
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sticky footer — save/cancel */}
        <div style={{
          flexShrink: 0, padding: '16px 24px',
          borderTop: '1px solid var(--border-subtle)',
          background: 'var(--bg-tertiary)',
          borderRadius: '0 0 12px 12px',
        }}>
          <div style={{ minHeight: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {saveStatus === 'saved' && (
              <div style={{ ...ST.statusMsg, color: 'var(--color-success)', margin: 0 }}>Changes saved successfully.</div>
            )}
            {saveStatus === 'error' && (
              <div style={{ ...ST.statusMsg, color: 'var(--color-error)', margin: 0 }}>Failed to save. Please try again.</div>
            )}
          </div>
          <div style={ST.saveRow}>
            <button type="button" style={ST.btnSecondary}
              onClick={handleReset} disabled={!isDirty || saving}>Reset</button>
            <button type="button"
              style={{
                ...ST.btnPrimary,
                opacity: isDirty && !saving && screenNameStatus !== 'taken' ? 1 : 0.5,
                cursor: isDirty && !saving && screenNameStatus !== 'taken' ? 'pointer' : 'not-allowed',
              }}
              onClick={handleSave}
              disabled={!isDirty || saving || screenNameStatus === 'taken'}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/*
        EULA review modal — opened from the Privacy & Security tab.
        Mounted at this level (sibling of the settings modal body) so
        it overlays cleanly on top of everything. The synthetic
        milestone evaluator picks up acceptance via the security cache
        invalidation already wired into `acceptEula()`.
      */}
      <EulaModal
        open={eulaOpen}
        onAccepted={() => setEulaOpen(false)}
        onAbort={() => setEulaOpen(false)}
      />
    </div>
  );
}

/* ================================================================== */
/*  Profile Tab Content                                                 */
/* ================================================================== */

function ProfileTabContent({
  user,
  formName, setFormName,
  formDisplayName, setFormDisplayName,
  formDateOfBirth, setFormDateOfBirth,
  formEmail, setFormEmail,
  formScreenName, setFormScreenName,
  screenNameStatus,
  formPrimaryLocation, setFormPrimaryLocation,
  formOperationalLocation, setFormOperationalLocation,
  communities,
  relationships,
  formShowHome, setFormShowHome,
  formShowOp, setFormShowOp,
  formIsSearchable, setFormIsSearchable,
  formContactability, setFormContactability,
  formDiscoveryAmp, setFormDiscoveryAmp,
}: {
  user: SecurityUserResponse | null;
  formName: string; setFormName: (v: string) => void;
  formDisplayName: string; setFormDisplayName: (v: string) => void;
  formDateOfBirth: string; setFormDateOfBirth: (v: string) => void;
  formEmail: string; setFormEmail: (v: string) => void;
  formScreenName: string; setFormScreenName: (v: string) => void;
  screenNameStatus: 'idle' | 'checking' | 'available' | 'taken';
  formPrimaryLocation: string; setFormPrimaryLocation: (v: string) => void;
  formOperationalLocation: string; setFormOperationalLocation: (v: string) => void;
  communities: GeoEntity[];
  relationships: any[];
  formShowHome: boolean; setFormShowHome: (v: boolean) => void;
  formShowOp: boolean; setFormShowOp: (v: boolean) => void;
  formIsSearchable: boolean; setFormIsSearchable: (v: boolean) => void;
  formContactability: string; setFormContactability: (v: string) => void;
  formDiscoveryAmp: boolean; setFormDiscoveryAmp: (v: boolean) => void;
}) {
  const { t } = useTranslation();
  if (!user) return null;

  return (
    <div style={{ paddingTop: 16 }}>
      <div style={ST.section}>
        <div style={ST.sectionTitle}>Profile Information</div>

        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Full Name</label>
          <input style={ST.fieldInput} value={formName}
            onChange={(e) => setFormName(e.target.value)}
            placeholder="Legal or full name" />
        </div>

        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Display Name</label>
          <input style={ST.fieldInput} value={formDisplayName}
            onChange={(e) => setFormDisplayName(e.target.value)}
            placeholder="How you appear to others" />
        </div>

        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Date of Birth</label>
          <div style={{ flex: 1 }}>
            <input
              type="date"
              style={ST.fieldInput}
              value={formDateOfBirth}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setFormDateOfBirth(e.target.value)}
            />
            <span style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4, display: 'block' }}>
              Your age determines what you can see and do. Life4Earth gives members under 18 a protected experience.
            </span>
            {(() => {
              if (!formDateOfBirth) return null;
              const d = new Date(formDateOfBirth);
              if (Number.isNaN(d.getTime())) return null;
              const now = new Date();
              let age = now.getFullYear() - d.getFullYear();
              const m = now.getMonth() - d.getMonth();
              if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
              if (age < 13) {
                return (
                  <span style={{ fontSize: 12, color: '#D53541', marginTop: 6, display: 'block' }}>
                    Life4Earth members must be at least 13.
                  </span>
                );
              }
              if (age >= 18) {
                return (
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6, display: 'block' }}>
                    By attesting that you are 18 or older, you acknowledge that while Life4Earth makes every
                    effort to keep content appropriate, you may be exposed to mature themes or comments.
                  </span>
                );
              }
              return null;
            })()}
          </div>
        </div>

        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Handle</label>
          <div style={{ flex: 1 }}>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute', insetInlineStart: 12, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--accent-primary)', fontWeight: 600, fontSize: 14,
              }}>@</span>
              <input
                style={{ ...ST.fieldInput, paddingInlineStart: 28 }}
                value={formScreenName}
                onChange={(e) => setFormScreenName(e.target.value.replace(/[^a-zA-Z0-9_]/g, '').toLowerCase())}
                placeholder="yourhandle"
                maxLength={30}
              />
            </div>
            {screenNameStatus === 'checking' && (
              <span style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 4, display: 'block' }}>Checking availability...</span>
            )}
            {screenNameStatus === 'available' && (
              <span style={{ fontSize: 12, color: '#45BB47', marginTop: 4, display: 'block' }}>Available</span>
            )}
            {screenNameStatus === 'taken' && (
              <span style={{ fontSize: 12, color: '#D53541', marginTop: 4, display: 'block' }}>This handle is already taken</span>
            )}
          </div>
        </div>

        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Email</label>
          <input style={ST.fieldInput} type="email" value={formEmail}
            onChange={(e) => setFormEmail(e.target.value)}
            placeholder="your@email.com" />
        </div>

        {/* Home Community */}
        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Home Community</label>
          <div style={{ flex: 1 }}>
            <GeoLocationPicker
              value={formPrimaryLocation}
              onChange={setFormPrimaryLocation}
              entities={communities}
              relationships={relationships}
              placeholder="Type a city, state, or country…"
            />
          </div>
        </div>

        {/* Operational Community */}
        <div style={ST.fieldRow}>
          <label style={ST.fieldLabel}>Operational Community</label>
          <div style={{ flex: 1 }}>
            <GeoLocationPicker
              value={formOperationalLocation}
              onChange={setFormOperationalLocation}
              entities={communities}
              relationships={relationships}
              placeholder="Type a city, state, or country…"
            />
          </div>
        </div>

        {/* Visibility toggles */}
        <div style={{ ...ST.fieldRow, alignItems: 'center' }}>
          <label style={ST.fieldLabel}>Show home community</label>
          <ToggleSwitch checked={formShowHome} onChange={setFormShowHome} />
        </div>
        <div style={{ ...ST.fieldRow, alignItems: 'center' }}>
          <label style={ST.fieldLabel}>Show op. community</label>
          <ToggleSwitch checked={formShowOp} onChange={setFormShowOp} />
        </div>
        <div style={{ ...ST.fieldRow, alignItems: 'center' }}>
          <label style={ST.fieldLabel}>Appear in search</label>
          <ToggleSwitch checked={formIsSearchable} onChange={setFormIsSearchable} />
        </div>

        {/* Feed Member Discovery privacy dials (migration 045). These are
            distinct from "Appear in search" (roster/search visibility) —
            keep them separate in copy: one is being pushed into fellow
            members' feeds, the other is who may request you as a contact. */}
        <div style={{ ...ST.fieldRow, alignItems: 'center' }}>
          <label style={ST.fieldLabel}>{t('userProfile:discoveryVisibility.label')}</label>
          <ToggleSwitch checked={formDiscoveryAmp} onChange={setFormDiscoveryAmp} />
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: -6, marginBottom: 10 }}>
          {t('userProfile:discoveryVisibility.description')}
        </div>

        <div style={{ ...ST.fieldRow, alignItems: 'center' }}>
          <label style={ST.fieldLabel}>{t('userProfile:contactability.label')}</label>
          <select
            style={ST.fieldInput}
            value={formContactability}
            onChange={(e) => setFormContactability(e.target.value)}
          >
            <option value="anyone">{t('userProfile:contactability.optAnyone')}</option>
            <option value="members_of_my_communities">{t('userProfile:contactability.optCommunities')}</option>
            <option value="contacts_of_contacts">{t('userProfile:contactability.optContactsOfContacts')}</option>
            <option value="no_one">{t('userProfile:contactability.optNoOne')}</option>
          </select>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: -6, marginBottom: 4 }}>
          {t('userProfile:contactability.description')}
        </div>
      </div>

      {/* Bio moved to the profile About tab's inline editor (Skip,
          2026-07-07) — it was buried here at the bottom of the modal
          while complete_profile required it. The About-tab pencil is
          now the single entry point; this modal no longer reads or
          writes `notes` (backend leaves the field untouched when the
          save payload omits it). */}
    </div>
  );
}

/* ================================================================== */
/*  Toggle Switch                                                       */
/* ================================================================== */

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div
      role="switch"
      aria-checked={checked}
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(!checked); } }}
      style={{
        width: 36, height: 20, borderRadius: 10,
        background: checked ? 'var(--accent-primary)' : 'var(--bg-active)',
        cursor: 'pointer', position: 'relative',
        transition: 'background 150ms ease', flexShrink: 0,
        border: checked ? 'none' : '1px solid var(--border-default)',
      }}
    >
      <div style={{
        width: 16, height: 16, borderRadius: '50%',
        background: '#fff',
        position: 'absolute', top: checked ? 2 : 1, left: checked ? 18 : 2,
        transition: 'left 150ms ease',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

/* ================================================================== */
/*  Theme Switcher                                                      */
/* ================================================================== */

const SUPPORTED_LANGUAGES = [
  { code: 'en', flag: '\uD83C\uDDFA\uD83C\uDDF8', defaultCalendar: 'gregorian' as const },
  { code: 'ar', flag: '\uD83C\uDDF8\uD83C\uDDE6', defaultCalendar: 'hijri' as const },
  { code: 'fr', flag: '\uD83C\uDDEB\uD83C\uDDF7', defaultCalendar: 'gregorian' as const },
  { code: 'de', flag: '\uD83C\uDDE9\uD83C\uDDEA', defaultCalendar: 'gregorian' as const },
  { code: 'uk', flag: '\uD83C\uDDFA\uD83C\uDDE6', defaultCalendar: 'gregorian' as const },
  { code: 'sv', flag: '\uD83C\uDDF8\uD83C\uDDEA', defaultCalendar: 'gregorian' as const },
  { code: 'ru', flag: '\uD83C\uDDF7\uD83C\uDDFA', defaultCalendar: 'gregorian' as const },
  { code: 'es', flag: '\uD83C\uDDEA\uD83C\uDDF8', defaultCalendar: 'gregorian' as const },
] as const;

function ThemeSwitcher({ value, onChange, availableThemes }: { value: string; onChange: (v: string) => void; availableThemes: Array<{ name: string; isCustom: boolean; baseTheme?: 'dark' | 'light'; displayName?: string; icon?: string }> }) {
  const { t } = useTranslation();

  const themeIcon = (theme: { name: string; isCustom: boolean; baseTheme?: string; icon?: string }) => {
    if (theme.icon) return theme.icon;
    const base = theme.isCustom ? theme.baseTheme : theme.name;
    return base === 'light' ? '\u2600\uFE0F' : '\uD83C\uDF19';
  };

  const themeLabel = (theme: { name: string; isCustom: boolean; displayName?: string }) => {
    if (theme.displayName) return theme.displayName;
    if (!theme.isCustom) return t(`theme:${theme.name}`);
    return theme.name;
  };

  return (
    <div style={SETTINGS_CARD}>
      <div style={SETTINGS_LABEL}>{t('theme:theme')}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={SETTINGS_SELECT}>
        {availableThemes.map((th) => (
          <option key={th.name} value={th.name}>
            {themeIcon(th)}  {themeLabel(th)}
          </option>
        ))}
      </select>
    </div>
  );
}

function LanguageSwitcher({ value, onChange, onCalendarHint }: {
  value: string;
  onChange: (code: string) => void;
  onCalendarHint: (cal: string) => void;
}) {
  const { t } = useTranslation();

  const handleChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const code = e.target.value;
    onChange(code);
    const langDef = SUPPORTED_LANGUAGES.find((l) => l.code === code);
    if (langDef) onCalendarHint(langDef.defaultCalendar);
  }, [onChange, onCalendarHint]);

  return (
    <div style={SETTINGS_CARD}>
      <div style={SETTINGS_LABEL}>{t('theme:settings.language')}</div>
      <select value={value} onChange={handleChange} style={SETTINGS_SELECT}>
        {SUPPORTED_LANGUAGES.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {lang.flag}  {t(`theme:lang.${lang.code}`)}
          </option>
        ))}
      </select>
    </div>
  );
}

function CalendarPreference({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const { t } = useTranslation();

  return (
    <div style={SETTINGS_CARD}>
      <div style={SETTINGS_LABEL}>{t('theme:settings.calendar')}</div>
      <select value={value} onChange={(e) => onChange(e.target.value)} style={SETTINGS_SELECT}>
        <option value="gregorian">{'\uD83D\uDCC5'}  {t('theme:calendar.gregorian')}</option>
        <option value="hijri">{'\uD83D\uDD4C'}  {t('theme:calendar.hijri')}</option>
      </select>
    </div>
  );
}

/** §7.9 Start Page surface option keys (LOCKED set §7.9.4). 'feed' = the
 *  factory default (stored as empty/clear — same resolution as unset);
 *  'community' reveals the type-ahead. 'overview' is legacy-only: shown
 *  only while the saved value still says overview, never re-offerable. */
type StartPageMode = 'feed' | 'action_center' | 'my_impact' | 'discover' | 'community' | 'overview';

function modeForValue(value: string): StartPageMode {
  const v = (value || '').trim().toLowerCase();
  if (!v || v === 'feed') return 'feed';
  if (v === 'action_center' || v === 'my_impact' || v === 'discover' || v === 'overview')
    return v as StartPageMode;
  return 'community'; // a guid
}

function StartPageSwitcher({
  value,
  onChange,
  userGuid,
  communityMap,
  homeCommunityGuid,
  operationalCommunityGuid,
  entities,
  relationships,
}: {
  value: string;
  onChange: (v: string) => void;
  userGuid: string;
  communityMap: Map<string, any>;
  /** DEV-1019 — user's home community (security_users.primary_location_entity_guid). */
  homeCommunityGuid?: string | null;
  /** DEV-1019 — user's operational community (security_users.operational_community_guid). */
  operationalCommunityGuid?: string | null;
  /** All community entities + relationships, for the geo type-ahead. */
  entities: any[];
  relationships: any[];
}) {
  const { t } = useTranslation();
  // "A specific community…" chosen but nothing picked yet: the VALUE is
  // still empty (= feed), so a local flag keeps the picker visible until
  // the user selects a community (or switches away).
  const [communityModeIntent, setCommunityModeIntent] = useState(false);
  const derivedMode = modeForValue(value);
  const mode: StartPageMode =
    communityModeIntent && derivedMode === 'feed' ? 'community' : derivedMode;
  // Community picked while in 'community' mode; kept when value is a guid.
  const communityValue = derivedMode === 'community' ? value : '';

  // V2 community slot occupancies (officer roles like Club Secretary, Mayor).
  // The legacy stores below are on the pre-DEV-1193 role model and miss these,
  // so an active officer slot (e.g. Secretary of a Rotary club) wouldn't appear.
  // Same source the Engagements page uses for slot-based roles.
  const [slotCommunities, setSlotCommunities] = useState<{ guid: string; name: string; isGeo: boolean }[]>([]);
  useEffect(() => {
    if (!userGuid) return;
    let cancelled = false;
    fetchCommunitySlotOccupancies(userGuid)
      .then((rows) => {
        if (cancelled) return;
        setSlotCommunities(
          (rows || []).map((r) => ({
            guid: r.communityEntityGuid,
            name: r.communityName,
            isGeo: String(r.entityType || '').toLowerCase().startsWith('geo_'),
          })),
        );
      })
      .catch((err) => console.error('[AccountSettings] failed to load community slot occupancies', err));
    return () => { cancelled = true; };
  }, [userGuid]);

  // DEV-1019 — Options used to source from `communityMembershipStore`,
  // which is initialized as an empty array and never hydrated, so the
  // dropdown was always empty. Now we build the option list from three
  // real sources that the user has actually selected or been assigned to:
  //   1. Home community (Account Settings → Home community).
  //   2. Operational community (Account Settings → Operational community).
  //   3. Community role assignments (community_role_assignments table —
  //      surfaced via communityRoleStore).
  // Deduplicated on community GUID, sorted by display name.
  const options = useMemo(() => {
    const upperUser = (userGuid || '').toUpperCase();
    const result: { guid: string; name: string; isGeo: boolean }[] = [];
    const seen = new Set<string>();

    const addCandidate = (rawGuid: string | null | undefined) => {
      const g = String(rawGuid || '').trim();
      if (!g) return;
      const upper = g.toUpperCase();
      if (seen.has(upper)) return;
      seen.add(upper);
      const entity = communityMap.get(upper);
      const communityName = entity?.name || g;
      const isGeo = String(entity?.entity_type || '').toLowerCase().startsWith('geo_');
      const ancestors = resolveCommunityAncestors(g);
      const ancestorNames = ancestors.map((a: any) => a.name).reverse();
      const fullPath = ancestorNames.length > 0
        ? `${communityName}, ${ancestorNames.join(', ')}`
        : communityName;
      result.push({ guid: g, name: fullPath, isGeo });
    };

    // 1 + 2. Home + operational.
    addCandidate(homeCommunityGuid);
    addCandidate(operationalCommunityGuid);

    // 3. Community role assignments for this user.
    for (const r of communityRoleStore) {
      if (String(r.user_guid || '').toUpperCase() !== upperUser) continue;
      if (r.is_active === false) continue;
      addCandidate(String(r.community_entity_guid || ''));
    }

    // Legacy: any rows in communityMembershipStore (currently empty; left
    // in place for future when the membership table is wired up).
    for (const m of communityMembershipStore) {
      if (String(m.user_guid || '').toUpperCase() !== upperUser) continue;
      if (String(m.status || 'active').toLowerCase() !== 'active') continue;
      addCandidate(String(m.community_entity_guid || ''));
    }

    // 4. V2 community slot occupancies (officer roles, e.g. Club Secretary).
    //    These carry their own name + entity_type from the API, so they don't
    //    depend on communityMap being loaded.
    for (const s of slotCommunities) {
      const g = String(s.guid || '').trim();
      if (!g) continue;
      const upper = g.toUpperCase();
      if (seen.has(upper)) continue;
      seen.add(upper);
      result.push({ guid: g, name: s.name || g, isGeo: s.isGeo });
    }

    result.sort((a, b) => a.name.localeCompare(b.name));
    return result;
  }, [userGuid, communityMap, homeCommunityGuid, operationalCommunityGuid, slotCommunities]);

  // Inject only non-geo (org/community) affiliations as extra-selectable —
  // geo affiliations are already reachable through the geo search itself.
  const orgExtras = useMemo(
    () => options.filter((o) => o.isGeo === false).map(({ guid, name }) => ({ guid, name })),
    [options],
  );

  return (
    <div style={SETTINGS_CARD}>
      <div style={SETTINGS_LABEL}>{t('userProfile:settings.startPage.label')}</div>
      <div style={{ fontSize: 11, color: 'var(--text-tertiary)', lineHeight: 1.35, marginBottom: 6 }}>
        {t('userProfile:settings.startPage.hint')}
      </div>
      {/* §7.9.4 locked options: surfaces + "a specific community". Landing
          on a place or a surface, never a task — projects/roles rejected. */}
      <select
        value={mode}
        onChange={(e) => {
          const next = e.target.value as StartPageMode;
          setCommunityModeIntent(next === 'community');
          if (next === 'feed') onChange('');            // factory default (clear)
          else if (next === 'community') onChange(communityValue || '');
          else onChange(next);                           // surface key
        }}
        style={{
          width: '100%', padding: '8px 10px', borderRadius: 8, marginBottom: 8,
          background: 'var(--bg-input, var(--bg-primary))', color: 'var(--text-primary)',
          border: '1px solid var(--border-default)', fontSize: 13,
        }}
      >
        <option value="feed">{t('userProfile:settings.startPage.optionFeed')}</option>
        <option value="action_center">{t('userProfile:settings.startPage.optionActionCenter')}</option>
        <option value="my_impact">{t('userProfile:settings.startPage.optionMyImpact')}</option>
        <option value="discover">{t('userProfile:settings.startPage.optionDiscover')}</option>
        <option value="community">{t('userProfile:settings.startPage.optionCommunity')}</option>
        {/* Legacy 'overview' rows only — visible while still saved, never re-offered. */}
        {mode === 'overview' && (
          <option value="overview">{t('userProfile:settings.startPage.optionOverviewLegacy')}</option>
        )}
      </select>
      {mode === 'community' && (
        /* Same type-ahead as Home/Operational, but org communities you're
           affiliated with are also selectable. */
        <GeoLocationPicker
          value={communityValue}
          onChange={(g) => onChange(g || '')}
          entities={entities}
          relationships={relationships}
          extraSelectable={orgExtras}
          placeholder={t('userProfile:settings.startPage.communityPlaceholder')}
        />
      )}
    </div>
  );
}
